import * as React from 'react';
import { SharedLegalPage } from './_shared/SharedLegalPage';

export const AIManifesto: React.FC = () => {
  return (
    <SharedLegalPage label="AI Manifesto">
      <h2>Introduction</h2>
      <p>
        This document outlines the JourneyApps Platform's approach to all forms of AI specifically including that of
        generative AI. This document specifically outlines various axioms that all JourneyApps staff adhere to when
        extending the product or making product changes.
      </p>

      <h2>AI should be implemented safely</h2>
      <p>AI should not generate content that is harmful in nature. From a development standpoint this means:</p>
      <ul>
        <li>Not generating harmful content displayed in an application.</li>
        <li>Not generating features that can harm end-users.</li>
        <li>
          Not generating harmful metadata that is associated with end-user-facing data. Examples include fabricated
          references, dates, uuids and urls.
        </li>
        <li>Not generating audit or logging information.</li>
        <li>Not generating policies or roles outside of the scope of the current operating policies.</li>
      </ul>
      <p>
        AI should never have direct control over operations that can cause a major business impact or lead to the cause
        of outages for customers:
      </p>
      <ul>
        <li>Not having control over deployment operations.</li>
        <li>Not having control over commit-to-master operations directly.</li>
        <li>
          Not having control over irreversible/destructive actions. Examples include: Deleting deployments, deleting
          users.
        </li>
        <li>Not assuming the role of a user or developer directly.</li>
      </ul>

      <h2>AI should always be scoped to a customer</h2>
      <p>
        Any AI models deployed, should always model the customers data and never cross reference external (other)
        customers data. That is to say that trained models should represent the data provided directly by a customer,
        and only their data.
      </p>

      <h2>It should always be clear what data was used for training in AI models.</h2>
      <p>Across the product, it should be clear what data was used (and how it was used) to train AI models.</p>

      <h2>AI should always be configurable</h2>
      <p>
        AI features should be made available across our products, but in a way where they are accessible in configurable
        ways. This means that:
      </p>
      <ul>
        <li>AI features can be turned on or off depending on developer and customer preference.</li>
        <li>
          AI features can be hard-disabled at an application, organization or customer level, and at a customers
          discretion.
        </li>
        <li>
          Where applicable, the impact of AI capability and breadth of scope in which it can perform operations, should
          be configurable in levels.
        </li>
      </ul>

      <h2>AI should compliment traditional development, not replace it</h2>
      <p>
        Where AI is implemented as developer tooling, it should always be an additive feature that compliments
        traditional development. AI should never take-over development environments and make it harder to get to
        traditional coding, rather traditional coding should stay as accessible as it has always existed in the product,
        while allowing AI to also flourish in its own right.
      </p>

      <h2>AI should co-exist with smart algorithmic assistive tools</h2>
      <p>
        AI technology should be deployed <i>alongside</i> smart tools that are highly deterministic. While generative AI
        can accomplish almost any coding task, it is always non-deterministic in nature. Developers should have always
        have a choice for a less intelligent (but smart alternative) that <b>is</b> deterministic. Examples include:
      </p>
      <ul>
        <li>Simple wizard that can generate forms and tables from a selected schema model.</li>
        <li>
          Autocompletion and intellisense that IS predictable and highly deterministic, such as being sorted
          alphabetically, or via appropriate type or grouping.
        </li>
      </ul>

      <h2>AI generated content must go through human review</h2>
      <p>
        AI generated content while accurate most of the time, is still non-deterministic by nature, and therefore should
        be marked as such, and require a human to approve or integrate it into an existing code-base. That is to say
        that a human should always sign off on AI generated content by some means.
      </p>
    </SharedLegalPage>
  );
};
